// PipelineStepEditModal — Reforma UX §6.8 / wireframe dlgPstep (PIPE-02/03).
//
// Editar etapa: nome · tipo de executor (agente/skill) · executor · timeout ·
// retry on fail. PIPE-03: a edição publica uma NOVA VERSÃO do pipeline via
// POST /v1/pipelines/:id/versions (createPipelineVersion). Versões são
// IMUTÁVEIS — o config da versão ativa é clonado, somente o step editado é
// substituído (sem inventar chaves fora do shape que o config real usa) e a
// nova versão vira a ativa (backend move latest_version_id).
//
// NOTA: a rota de versões ativa no backend pós-restart — até lá o POST
// retorna 404 e a UI degrada com mensagem clara (sem persistir nada).

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Label } from '@/components/ui/Label';
import { Pencil } from 'lucide-react';
import { createPipelineVersion } from '@/lib/api-pipelines';
import type { NormalizedStep } from './pipeline-metrics';

/**
 * Normaliza erro do POST /v1/pipelines/:id/versions pra mensagem de UI.
 * 404 = rota ainda não ativa no backend em produção (degrada com aviso
 * pós-restart); 409/422 = mensagem do backend (duplicada/config inválida).
 *
 * (Duplicada em PipelineNewVersionModal — exportar função daqui viola
 * react-refresh/only-export-components.)
 */
function versionRequestErrorMessage(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (/HTTP 404|not.?found|rota n[ãa]o encontrada/i.test(msg)) {
    return 'O endpoint de nova versão ainda não está ativo no backend — '
      + 'disponível após a atualização (restart) do backend. Nada foi alterado.';
  }
  return msg;
}

const EXECUTOR_TYPE_OPTIONS = [
  { value: 'agent', label: 'Agente' },
  { value: 'skill', label: 'Skill' },
];

const RETRY_OPTIONS = [
  { value: '0', label: '0 (sem retry)' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
];

interface StepEditFormValues {
  name: string;
  executorKind: 'agent' | 'skill';
  executorSlug: string;
  timeoutSeconds: string;
  retry: string;
}

interface BuiltVersion {
  config: Record<string, unknown>;
  changelog: string;
}

/**
 * Monta o config da NOVA versão: clone profundo do config da versão ativa
 * com SOMENTE o step editado substituído — todo o resto fica intacto.
 *
 * Localiza o step no array raw pela mesma chave que normalizeSteps derivou
 * (slug canonical v2 → id legacy → índice `step-${i}`). Só escreve chaves
 * que o shape do config real já usa:
 *   - canonical v2: name, executor.agent_slug / executor.skill_slug,
 *     timeout_seconds, retry_on_fail (apenas se já presente no step);
 *   - legacy: name, kind, target_slug, timeout_seconds.
 *
 * Retorna null com `error` quando o step não é localizável ou nada mudou.
 */
function buildEditedVersion(
  activeConfig: Record<string, unknown>,
  step: NormalizedStep,
  values: StepEditFormValues,
): { built: BuiltVersion | null; error: string | null } {
  const config = JSON.parse(JSON.stringify(activeConfig)) as Record<string, unknown>;
  const steps = config['steps'];
  if (!Array.isArray(steps)) {
    return { built: null, error: 'Config da versão ativa não tem steps[] — não dá pra editar.' };
  }

  let idx = steps.findIndex((s) => {
    if (!s || typeof s !== 'object') return false;
    const raw = s as Record<string, unknown>;
    return raw['slug'] === step.key || raw['id'] === step.key;
  });
  if (idx === -1) {
    // Fallback: key sintética `step-${i}` (entry sem slug/id) — usa o índice.
    const m = /^step-(\d+)$/.exec(step.key);
    if (m) idx = Number(m[1]);
  }
  const raw = steps[idx] as Record<string, unknown> | undefined;
  if (idx < 0 || idx >= steps.length || !raw || typeof raw !== 'object') {
    return { built: null, error: `Etapa "${step.key}" não encontrada no config da versão ativa.` };
  }

  const changes: string[] = [];

  // Nome (existe nos dois shapes).
  const newName = values.name.trim();
  if (newName && newName !== step.name) {
    raw['name'] = newName;
    changes.push(`nome "${step.name}" → "${newName}"`);
  }

  // Executor — canonical v2 (executor.{agent_slug,skill_slug}) OU legacy
  // (kind + target_slug). Não inventa shape: edita o que o step já usa.
  const newSlug = values.executorSlug.trim();
  if (!newSlug) {
    return { built: null, error: 'Executor (slug) é obrigatório.' };
  }
  const executorChanged =
    values.executorKind !== step.executorKind
    || newSlug !== (step.executorSlug === '—' ? '' : step.executorSlug);
  if (executorChanged) {
    const executor = raw['executor'];
    if (executor && typeof executor === 'object' && !Array.isArray(executor)) {
      const exec = executor as Record<string, unknown>;
      if (values.executorKind === 'skill') {
        // agent_slug (obrigatório no canonical) é preservado — só a skill muda.
        exec['skill_slug'] = newSlug;
      } else {
        exec['agent_slug'] = newSlug;
        // Sem skill_slug o step volta a ser executado direto pelo agente.
        delete exec['skill_slug'];
      }
    } else {
      // Legacy: kind + target_slug.
      raw['kind'] = values.executorKind === 'skill' ? 'skill' : 'agent';
      raw['target_slug'] = newSlug;
    }
    changes.push(
      `executor ${step.executorKind === 'skill' ? 'skill' : 'agente'}·${step.executorSlug}`
      + ` → ${values.executorKind === 'skill' ? 'skill' : 'agente'}·${newSlug}`,
    );
  }

  // Timeout (existe nos dois shapes).
  if (values.timeoutSeconds.trim() !== '') {
    const n = Number(values.timeoutSeconds);
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
      return { built: null, error: 'Timeout deve ser um inteiro ≥ 0 (segundos).' };
    }
    if (n !== step.timeoutSeconds) {
      raw['timeout_seconds'] = n;
      changes.push(`timeout ${step.timeoutSeconds ?? '—'}s → ${n}s`);
    }
  }

  // Retry — SOMENTE se o campo já existe no shape do step (canonical v2 não
  // persiste retry por padrão; não inventamos chave nova).
  const newRetry = Number(values.retry);
  if (typeof raw['retry_on_fail'] === 'number' && newRetry !== step.retryOnFail) {
    raw['retry_on_fail'] = newRetry;
    changes.push(`retry ${step.retryOnFail ?? 0} → ${newRetry}`);
  }

  if (changes.length === 0) {
    return { built: null, error: 'Nenhuma alteração para salvar.' };
  }

  return {
    built: {
      config,
      changelog: `Edição da etapa ${step.key}: ${changes.join(', ')}.`,
    },
    error: null,
  };
}

export interface PipelineStepEditModalProps {
  step: NormalizedStep | null;
  open: boolean;
  onClose: () => void;
  /** id (uuid) do pipeline — alvo do POST /v1/pipelines/:id/versions. */
  pipelineId: string;
  /** Config canonical da versão ativa — base imutável da nova versão. */
  activeConfig: Record<string, unknown> | undefined;
  /** Sucesso: parent toasta + refetch do detalhe (nova versão vira ativa). */
  onSaved: (message: string) => void;
}

/** Form interno — montado por etapa (key) pra inicializar estado sem effects. */
function StepEditForm({
  step,
  pipelineId,
  activeConfig,
  onSaved,
  onClose,
}: {
  step: NormalizedStep;
  pipelineId: string;
  activeConfig: Record<string, unknown> | undefined;
  onSaved: (message: string) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(step.name);
  const [executorKind, setExecutorKind] = useState<'agent' | 'skill'>(step.executorKind);
  const [executorSlug, setExecutorSlug] = useState(
    step.executorSlug === '—' ? '' : step.executorSlug,
  );
  const [timeoutSeconds, setTimeoutSeconds] = useState(
    step.timeoutSeconds !== null ? String(step.timeoutSeconds) : '',
  );
  const [retry, setRetry] = useState(step.retryOnFail !== null ? String(step.retryOnFail) : '0');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!activeConfig) {
      setError('Sem config da versão ativa carregado — não dá pra montar a nova versão.');
      return;
    }
    const { built, error: buildError } = buildEditedVersion(activeConfig, step, {
      name,
      executorKind,
      executorSlug,
      timeoutSeconds,
      retry,
    });
    if (!built) {
      setError(buildError);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // version omitida → backend bumpa minor da última.
      const res = await createPipelineVersion(pipelineId, {
        config: built.config,
        changelog: built.changelog,
      });
      onSaved(
        `Nova versão v${res.version} publicada com a edição da etapa "${step.name}" — `
        + 'ela agora é a versão ativa; as versões anteriores permanecem imutáveis no histórico.',
      );
      onClose();
    } catch (err) {
      setError(versionRequestErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="pstep-name">Nome da etapa</Label>
          <Input id="pstep-name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Tipo de executor"
            options={EXECUTOR_TYPE_OPTIONS}
            value={executorKind}
            onChange={(v) => setExecutorKind(v as 'agent' | 'skill')}
          />
          <div className="space-y-1.5">
            <Label htmlFor="pstep-exec">Executor (slug)</Label>
            <Input
              id="pstep-exec"
              value={executorSlug}
              onChange={(e) => setExecutorSlug(e.target.value)}
              placeholder={executorKind === 'agent' ? 'ex: dev' : 'ex: code-review'}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="pstep-timeout">Timeout (segundos)</Label>
            <Input
              id="pstep-timeout"
              type="number"
              min={0}
              value={timeoutSeconds}
              onChange={(e) => setTimeoutSeconds(e.target.value)}
            />
          </div>
          <Select
            label="Retry on fail"
            options={RETRY_OPTIONS}
            value={retry}
            onChange={setRetry}
          />
        </div>
        {error && (
          <p className="text-xs text-raios-danger-text bg-raios-danger/10 border border-raios-danger/30 rounded-md px-3 py-2">
            {error}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={submitting}>Cancelar</Button>
        <Button variant="brand" onClick={() => void handleSave()} disabled={submitting}>
          {submitting ? 'Publicando nova versão…' : 'Salvar etapa'}
        </Button>
      </DialogFooter>
    </>
  );
}

export function PipelineStepEditModal({
  step,
  open,
  onClose,
  pipelineId,
  activeConfig,
  onSaved,
}: PipelineStepEditModalProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="z-[450]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-4 w-4 text-raios-accent-text" />
            Editar etapa
          </DialogTitle>
          <DialogDescription>
            Salvar publica uma <strong>nova versão</strong> do pipeline (imutável), que se
            torna a versão ativa. As versões anteriores permanecem inalteradas no histórico.
          </DialogDescription>
        </DialogHeader>
        {step && (
          <StepEditForm
            key={step.key}
            step={step}
            pipelineId={pipelineId}
            activeConfig={activeConfig}
            onSaved={onSaved}
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
